// The spring maths the scroll engine's link glide is built from. In the sibling project these three helpers
// lived inside the imperative gesture controller (`transition-controller.ts`); danchiano only ports the
// scroll-snap engine, so they're extracted here as the small pure module the engine actually needs — no
// gesture controller comes along for the ride. Read-back is from the animation's OWN clock, never computed
// style, so a caught/re-grabbed glide always agrees with the pixels on screen.

import { TRANSITION_CONFIG } from './transitions.config'

const { spring } = TRANSITION_CONFIG

/**
 * How finely the spring is SAMPLED into keyframes — not a frame rate, and not a cap on one. Between two
 * samples the compositor interpolates a straight line, so sampling at 60Hz would hand a 120Hz display
 * piecewise-linear corners at exactly the rate it can resolve. 240Hz stays finer than any shipping panel,
 * and the cost is nothing (a ~350ms settle is ~84 keyframes).
 */
export const SPRING_HZ = 240
/** Hard cap on a settle (3s at SPRING_HZ) — a spring that hasn't arrived by then never will. */
const SPRING_MAX_STEPS = SPRING_HZ * 3

/**
 * Integrate the spring AHEAD of time, into the progress it will hold on each frame. A release/glide is not
 * interactive — its whole trajectory is determined the moment it starts — so it can be handed to the
 * compositor as keyframes, where a concurrent React commit CANNOT starve it. Sampled keyframes rather than a
 * `linear()` spring easing on purpose: `linear()` is too new for some browsers this app still ships to;
 * linearly-interpolated keyframes work everywhere `Element.animate` does.
 */
export function springSamples(from: number, target: number, velocity: number): number[] {
  const dt = 1 / SPRING_HZ
  const samples: number[] = [from]
  let p = from
  let v = velocity
  for (let i = 0; i < SPRING_MAX_STEPS; i++) {
    const accel = (-spring.stiffness * (p - target) - spring.damping * v) / spring.mass
    v += accel * dt
    p += v * dt
    // Settle on POSITION (within 1% of target ≈ a few px), not velocity: the tail of a near-critically-damped
    // spring hovers with tiny residual velocity for hundreds of ms.
    if (Math.abs(p - target) < 0.01) break
    samples.push(p)
  }
  samples.push(target)
  return samples
}

/**
 * Where a composited spring animation has actually got to — read back from the animation's OWN clock
 * (`currentTime` → sample index), never from computed style: one source of truth, no layout read, so a
 * re-grab/catch hands a baseline that exactly matches the pixels. `fallback` covers the moments before/after
 * an animation exists.
 */
export function springProgressAt(lead: Animation | undefined, samples: readonly number[], fallback: number): number {
  if (!lead || samples.length === 0) return fallback
  const elapsed = Number(lead.currentTime ?? 0)
  const index = Math.round((elapsed / 1000) * SPRING_HZ)
  const clamped = index < 0 ? 0 : index > samples.length - 1 ? samples.length - 1 : index
  return samples[clamped] ?? fallback
}
